/**
 * Auth transport — the three REST calls the auth flow makes (E10-F01-T05).
 *
 * Endpoint parity with the legacy n8n `_common` session lifecycle (Switch
 * Command → /auth domain, architecture.md §6 mapping table; legacy nodes
 * `DB: Get User` / `DB: Create Session` / `DB: Validate Session1` /
 * `DB: Delete Session`, docs/source-n8n/reference/Users.md):
 *
 *   POST /auth/login   → `{ session_token, user }`  (Get User + Create Session)
 *   GET  /auth/me      → `AuthIdentity`              (Validate Session1)
 *   POST /auth/logout  → 204 / no content            (Delete Session)
 *
 * These are the contract paths the web auth flow calls; the Fastify `/auth`
 * router (E01) honours them. Every call goes through the typed `apiClient`, so:
 *   - the Bearer token is auto-injected (login sends none — there is none yet);
 *   - the response is normalized to `ApiResult<T>` and NEVER throws;
 *   - a 401 from `GET /auth/me` (expired/invalid token) is already routed through
 *     `handleUnauthorized` by the client, so a failed restore self-cleans.
 *
 * The shapes are validated with the @eer/shared schemas at the boundary so a
 * malformed login/me response cannot put a half-typed principal into the session
 * cache. Web never imports `zod` directly — every schema/contract comes through
 * @eer/shared (the single source of truth, the only package that declares zod).
 */
import {
  authIdentitySchema,
  loginResponseSchema,
  type ApiResult,
  type AuthIdentity,
  type LoginResponse,
} from '@eer/shared';
import { apiClient } from '../api/client.js';

/** Credentials posted to `POST /auth/login` (legacy `body.email`/`body.password`). */
export interface LoginCredentials {
  readonly email: string;
  readonly password: string;
}

// `loginResponseSchema` / `LoginResponse` are the shared `POST /auth/login`
// contract (defined in @eer/shared/auth) — re-exported here for the auth flow's
// existing import sites.
export { loginResponseSchema };
export type { LoginResponse };

/** Authenticate with email + password. Returns `{ session_token, user }`. */
export async function login(credentials: LoginCredentials): Promise<ApiResult<LoginResponse>> {
  const res = await apiClient.post<unknown>('/auth/login', credentials, { command: 'login' });
  if (!res.ok) return res;
  const parsed = loginResponseSchema.safeParse(res.data);
  if (!parsed.success) {
    return {
      ok: false,
      error: {
        code: 'INTERNAL',
        message: 'The server returned an invalid response while signing in.',
      },
    };
  }
  return { ok: true, data: parsed.data };
}

/**
 * Validate the persisted session token against the server (`GET /auth/me`,
 * legacy `Validate Session1`). On success returns the fresh principal; on a 401
 * the client has already fired `handleUnauthorized`, and this returns the typed
 * error so the restore flow falls through to login.
 */
export async function fetchMe(): Promise<ApiResult<AuthIdentity>> {
  const res = await apiClient.get<unknown>('/auth/me', { command: 'validate_session' });
  if (!res.ok) return res;
  const parsed = authIdentitySchema.safeParse(res.data);
  if (!parsed.success) {
    return {
      ok: false,
      error: { code: 'UNAUTHENTICATED', message: 'The session is invalid.' },
    };
  }
  return { ok: true, data: parsed.data };
}

/**
 * Best-effort server-side logout (`POST /auth/logout`, legacy `Delete Session`).
 * The caller clears local state regardless of the outcome, so a network/serer
 * failure here is intentionally NOT surfaced — logout must always succeed
 * locally (acceptance: "logout делает best-effort серверный logout и локально
 * чистит те же ключи").
 */
export async function logoutRequest(): Promise<void> {
  try {
    await apiClient.post('/auth/logout', undefined, { command: 'logout' });
  } catch {
    /* best-effort — local clear happens in the caller no matter what */
  }
}
